import React from "react";
import { Link } from "react-router-dom";

const services = [
  {
    id: "early-intervention", num: "01", title: "Early Intervention",
    intro: "For children, young people and adults who may be at risk of justice system involvement. We identify challenges early and connect people with the right supports.",
    items: ["Mentoring", "Life skills development", "Education and employment support", "Goal planning", "Community connection", "Referrals to specialist services", "Family support", "Crisis intervention and response"],
  },
  {
    id: "justice-support", num: "02", title: "Justice Support",
    intro: "Support throughout involvement with the justice system, including those on bail, community based orders, parole, and during incarceration.",
    items: ["Face to face prison visits", "Short notice prison welfare visits where available", "Advocacy", "Wellbeing support", "Reintegration and release planning", "Practical assistance", "Ongoing case management", "Emotional support"],
  },
  {
    id: "reintegration", num: "03", title: "Reintegration",
    intro: "Returning to the community can be challenging. We assist individuals to establish stability following release.",
    items: ["Housing support", "Employment and education pathways", "Centrelink assistance", "Community connection", "Ongoing mentoring", "Referrals to specialist services", "Practical support to reduce the risk of reoffending", "Transition support to re-engage back into society"],
  },
  {
    id: "family-carer", num: "04", title: "Family & Carer Support",
    intro: "Justice system involvement affects entire families. We provide support, education, advocacy and practical guidance.",
    items: ["Support for partners and parents", "Support for children of incarcerated parents", "Carer wellbeing and self-care", "Advocacy with schools and services", "Referrals to family-focused programs", "Practical guidance"],
  },
  {
    id: "social-work", num: "05", title: "Social Work Services",
    intro: "Delivered by qualified social workers using trauma-informed, evidence-based practice.",
    items: ["Counselling and supportive interventions", "Trauma informed care", "Psychosocial assessments", "Case management", "Crisis intervention and crisis response", "Safety planning", "Advocacy", "Professional report writing", "Support letters and referrals", "Service coordination", "Case conferencing", "Goal planning", "Referrals to housing, mental health, AOD, financial, legal, employment and education services"],
  },
  {
    id: "advocacy", num: "06", title: "Advocacy & System Navigation",
    intro: "Navigating the justice system can be overwhelming, particularly for people who have experienced trauma or previous negative experiences with authorities. We help you understand it, navigate it, and ensure you never have to face it alone.",
    items: ["Communication with police, corrective services and legal representatives", "Support with government agencies, schools and employers", "Preparing for important meetings and appointments", "Explaining complex processes", "Ensuring you understand your rights, responsibilities and options"],
  },
  {
    id: "peer-support", num: "07", title: "Peer Support",
    intro: "Alongside qualified social workers, our team includes peer workers with lived experience of the justice system and overcoming adversity.",
    items: ["Support from workers with lived experience", "Grounded in genuine understanding", "Compassionate and practical", "A visible sign that change is possible"],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="fade-in">
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-16">
        <div className="njas-eyebrow">Services</div>
        <div className="njas-script text-5xl text-njas-sage-deep mt-6">Seven pillars,</div>
        <h1 className="njas-display text-6xl sm:text-8xl lg:text-9xl text-njas-navy mt-2 leading-[0.88] max-w-5xl">
          ONE&nbsp;STEADY<br/>HAND.
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-njas-muted leading-relaxed">
          Trauma informed, person centred, evidence based. Our services are shaped around every
          individual's needs, goals and circumstances.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pb-24 grid lg:grid-cols-12 gap-10">
        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-28 pl-6 border-l border-[var(--njas-sage)]/50">
            <div className="njas-eyebrow mb-6">On this page</div>
            <ul className="space-y-3">
              {services.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="njas-condensed text-[12px] tracking-[0.18em] font-semibold text-njas-navy hover:text-njas-sage-deep njas-link">
                    {s.num}  ·  {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="lg:col-span-9">
          {services.map((s, idx) => (
            <article key={s.id} id={s.id} className="scroll-mt-24 py-12 border-t border-[var(--njas-sage)]/50 first:border-t-0" data-testid={`service-${s.id}`}>
              <div className="grid grid-cols-12 gap-6 items-baseline">
                <div className="col-span-2 njas-display text-4xl text-njas-sage">{s.num}</div>
                <h2 className="col-span-10 njas-display text-5xl sm:text-6xl text-njas-navy leading-[0.9]">{s.title}</h2>
              </div>
              <p className="mt-6 ml-0 sm:ml-[16.6%] text-njas-text leading-relaxed max-w-3xl">{s.intro}</p>
              <ul className="mt-8 ml-0 sm:ml-[16.6%] grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm text-njas-text">
                {s.items.map((it) => (
                  <li key={it} className="flex gap-3 border-b border-[var(--njas-line)] pb-2">
                    <span className="njas-condensed text-njas-sage font-bold">◆</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              {idx === services.length - 1 && <div className="border-b border-[var(--njas-sage)]/50 mt-8" />}
            </article>
          ))}
        </div>
      </section>

      <section className="bg-[var(--njas-bg-alt)] border-y border-[var(--njas-line)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="njas-script text-4xl text-njas-sage-deep">Ready to reach out?</div>
          <h3 className="njas-display text-5xl sm:text-6xl text-njas-navy mt-2">MAKE&nbsp;A REFERRAL.</h3>
          <p className="text-njas-muted mt-6 max-w-xl mx-auto">
            Submit a confidential referral for yourself, a loved one, or someone in your service.
          </p>
          <Link to="/refer" data-testid="services-refer-cta" className="mt-8 inline-block njas-condensed tracking-[0.22em] font-semibold text-[13px] px-8 py-4 bg-njas-navy text-njas-bg hover:bg-njas-navy-hover transition-colors">
            Start a Referral
          </Link>
        </div>
      </section>
    </div>
  );
}
